import { useCallback, useState } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import * as Dialog from '@radix-ui/react-dialog'
import { HugeiconsIcon } from '@hugeicons/react'
import { PlusSignIcon } from '@hugeicons/core-solid-rounded'

import * as Form from '@/features/form'
import { dialogContainer, DialogHeader } from '@/layouts/Dialog'
import { ButtonRowForm } from './ButtonRowForm'
import { FormData } from '../hooks/useUploadButtonRow'
import { ButtonRow } from '../types'
import { Suspense } from '@/components/ui/Suspense'

type Props = {
  value?: ButtonRow | null
  onChange: (value: ButtonRow) => void
}

export function ButtonRowInput({ value, onChange }: Props) {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)

  const onUpdate = useCallback(
    (data: FormData) => {
      // We treat changed ButtonRows like completely new fields
      onChange({
        ...data,
        isDirty: true
      })

      setOpen(false)
    },
    [onChange, setOpen]
  )

  return (
    <Form.DirtyProvider>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        {!value && (
          <Dialog.Trigger asChild>
            <button
              type="button"
              className="flex flex-row justify-center items-center p-2 border border-dashed border-text-light rounded-small gap-1"
            >
              <HugeiconsIcon
                icon={PlusSignIcon}
                className="size-5 text-text-light"
              />
              <span className="text-text-light text-regular">
                <Trans>Add Button Row</Trans>
              </span>
            </button>
          </Dialog.Trigger>
        )}
        {value && (
          <Dialog.Trigger asChild>
            <button type="button">{value.header}</button>
          </Dialog.Trigger>
        )}
        <Dialog.Portal container={dialogContainer}>
          <Dialog.Overlay className="fixed size-full inset-0 bg-black/30 grid place-items-center overflow-y-auto z-top-dialog">
            <Dialog.Content className="relative w-screen max-w-[700px] py-10 px-2">
              <div className="shadow-md rounded bg-white flex flex-col gap-4 p-5">
                <Suspense>
                  <DialogHeader
                    title={t`Button Row`}
                    description={t`Add/Edit a button row that is shown in the Guest-App`}
                    close={() => setOpen(false)}
                  />
                  <ButtonRowForm
                    value={value}
                    onDone={onUpdate}
                    onCancel={() => setOpen(false)}
                  />
                </Suspense>
              </div>
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </Form.DirtyProvider>
  )
}
